import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { taskService, Task } from '../lib/taskService';

interface TaskCache {
  [opportunityId: string]: {
    tasks: Task[];
    timestamp: number;
    loading: boolean;
  };
}

interface TaskCacheContextType {
  getTasks: (opportunityId: string, forceRefresh?: boolean) => Promise<Task[]>;
  preloadTasks: (opportunityId: string) => Promise<void>;
  invalidateCache: (opportunityId: string) => void;
  isLoading: (opportunityId: string) => boolean;
}

const TaskCacheContext = createContext<TaskCacheContextType | undefined>(undefined);

const CACHE_DURATION = 30000;

export function TaskCacheProvider({ children }: { children: React.ReactNode }) {
  const [cache, setCache] = useState<TaskCache>({});
  const loadingRef = useRef<{ [key: string]: Promise<Task[]> }>({});

  const getTasks = useCallback(async (opportunityId: string, forceRefresh = false): Promise<Task[]> => {
    const now = Date.now();
    const cached = cache[opportunityId];

    if (!forceRefresh && cached && (now - cached.timestamp) < CACHE_DURATION && !cached.loading) {
      return cached.tasks;
    }

    if (loadingRef.current[opportunityId]) {
      return loadingRef.current[opportunityId];
    }

    setCache(prev => ({
      ...prev,
      [opportunityId]: { tasks: cached?.tasks || [], timestamp: now, loading: true }
    }));

    const loadPromise = taskService.getTasks(opportunityId);
    loadingRef.current[opportunityId] = loadPromise;

    try {
      const tasks = await loadPromise;
      setCache(prev => ({
        ...prev,
        [opportunityId]: { tasks, timestamp: Date.now(), loading: false }
      }));
      return tasks;
    } catch (error) {
      console.error('Error loading tasks:', error);
      setCache(prev => ({
        ...prev,
        [opportunityId]: { tasks: cached?.tasks || [], timestamp: now, loading: false }
      }));
      return cached?.tasks || [];
    } finally {
      delete loadingRef.current[opportunityId];
    }
  }, [cache]);

  const preloadTasks = useCallback(async (opportunityId: string) => {
    const cached = cache[opportunityId];
    const now = Date.now();

    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      return;
    }

    await getTasks(opportunityId);
  }, [cache, getTasks]);

  const invalidateCache = useCallback((opportunityId: string) => {
    setCache(prev => {
      const newCache = { ...prev };
      delete newCache[opportunityId];
      return newCache;
    });
  }, []);

  const isLoading = useCallback((opportunityId: string): boolean => {
    return cache[opportunityId]?.loading || false;
  }, [cache]);

  return (
    <TaskCacheContext.Provider value={{ getTasks, preloadTasks, invalidateCache, isLoading }}>
      {children}
    </TaskCacheContext.Provider>
  );
}

export function useTaskCache() {
  const context = useContext(TaskCacheContext);
  if (!context) {
    throw new Error('useTaskCache must be used within TaskCacheProvider');
  }
  return context;
}
